import { useMemo } from 'react'
import type { OpenTab } from '@/components/features/document-tabs'
import type { ExtensionDefinition } from '@/types/extension'
import {
  REGISTERED_EXTENSIONS,
  getExtensionForTabType,
  isExtensionTabType,
} from '@/config/integrations'

/**
 * Return type for useExtensionSupport hook
 */
export interface UseExtensionSupportReturn {
  /** Current extension for active tab (if any) */
  currentExtension: ExtensionDefinition | null

  /** Check if tab is handled by an extension */
  isExtensionTab: (tab: OpenTab) => boolean

  /** Get extension for specific tab type */
  getExtensionForType: (type: OpenTab['type']) => ExtensionDefinition | null

  /** All registered extensions */
  registeredExtensions: ExtensionDefinition[]

  /** Whether current tab is an extension tab */
  isCurrentTabExtension: boolean
}

/**
 * Hook to support dynamic extension rendering in document viewer
 *
 * This hook provides extension lookup functionality without hard-coding
 * extension-specific logic in core components.
 *
 * @param activeTab - Currently active tab (or null if no tab)
 * @returns Extension support utilities
 *
 * @example
 * ```tsx
 * const { currentExtension, isCurrentTabExtension } = useExtensionSupport(activeTab)
 *
 * if (currentExtension?.ViewerComponent) {
 *   return <currentExtension.ViewerComponent tab={activeTab} ... />
 * }
 * ```
 */
export const useExtensionSupport = (
  activeTab: OpenTab | null
): UseExtensionSupportReturn => {
  /**
   * Lookup extension for current tab
   * Memoized to avoid re-computing on every render
   */
  const currentExtension = useMemo(() => {
    if (!activeTab) return null
    return getExtensionForTabType(activeTab.type) || null
  }, [activeTab?.type])

  /**
   * Check if any tab is handled by an extension
   */
  const isExtensionTab = (tab: OpenTab): boolean => {
    return isExtensionTabType(tab.type)
  }

  /**
   * Get extension for specific tab type
   */
  const getExtensionForType = (type: OpenTab['type']): ExtensionDefinition | null => {
    return getExtensionForTabType(type) || null
  }

  /**
   * Whether current tab is an extension tab
   */
  const isCurrentTabExtension = useMemo(() => {
    return currentExtension !== null
  }, [currentExtension])

  return {
    currentExtension,
    isExtensionTab,
    getExtensionForType,
    registeredExtensions: REGISTERED_EXTENSIONS,
    isCurrentTabExtension,
  }
}
